import type { FreeToolCategory, FreeToolsApiResponse } from './types'
import { FREE_TOOLS_FALLBACK } from './data/fallback'

const API_URL = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '')

/**
 * Global dedup helper — ensures each tool id and url appears once across
 * all categories regardless of whether it came from the API or fallback.
 * This fixes the historical bug where ad-creative-analyzer appeared in
 * both cat-dev and cat-generation, showing 20 instead of 19 internal.
 */
function dedupCategories(cats: FreeToolCategory[]): FreeToolCategory[] {
  const globalIdSeen = new Set<string>()
  const globalUrlSeen = new Set<string>()
  const sorted = [...cats].sort((a, b) => a.sortOrder - b.sortOrder)
  const result: FreeToolCategory[] = []
  for (const cat of sorted) {
    const tools = [...(cat.tools ?? [])].sort((a, b) => a.sortOrder - b.sortOrder)
    const deduped: typeof tools = []
    for (const t of tools) {
      if (globalIdSeen.has(t.id)) continue
      if (globalUrlSeen.has(t.url)) continue
      globalIdSeen.add(t.id)
      globalUrlSeen.add(t.url)
      deduped.push(t)
    }
    if (deduped.length === 0) continue
    result.push({ ...cat, tools: deduped, count: deduped.length })
  }
  return result
}

/**
 * Merge API response + static fallback so that new tools added only in
 * fallback remain visible even when the remote D1 hasn't run the latest
 * migration yet. API categories take precedence.
 */
function mergeWithFallback(apiCats: FreeToolCategory[]): FreeToolCategory[] {
  const byId = new Map<string, FreeToolCategory>()
  for (const c of apiCats) {
    byId.set(c.id, { ...c, tools: [...(c.tools ?? [])] })
  }
  for (const fbCat of FREE_TOOLS_FALLBACK) {
    const existing = byId.get(fbCat.id)
    if (!existing) {
      byId.set(fbCat.id, { ...fbCat, tools: [...fbCat.tools] })
      continue
    }
    const existingIds = new Set(existing.tools.map((t) => t.id))
    const existingUrls = new Set(existing.tools.map((t) => t.url))
    for (const fbTool of fbCat.tools) {
      if (existingIds.has(fbTool.id)) continue
      if (existingUrls.has(fbTool.url)) continue
      existing.tools.push(fbTool)
      existingIds.add(fbTool.id)
      existingUrls.add(fbTool.url)
    }
  }
  return dedupCategories([...byId.values()])
}

export async function fetchFreeToolsCategories(signal?: AbortSignal): Promise<FreeToolCategory[]> {
  if (!API_URL) {
    return dedupCategories(FREE_TOOLS_FALLBACK)
  }
  const timeoutCtrl = new AbortController()
  // prod + cold start from localhost/Cairo needs >4s — 12s prevents false fallback on healthy data
  const timeoutId = setTimeout(() => timeoutCtrl.abort(), 12000)
  const combinedSignal = signal
    ? (() => {
        if (typeof (AbortSignal as any).any === 'function') {
          return (AbortSignal as any).any([signal, timeoutCtrl.signal]) as AbortSignal
        }
        signal.addEventListener('abort', () => timeoutCtrl.abort(), { once: true })
        return timeoutCtrl.signal
      })()
    : timeoutCtrl.signal
  try {
    const res = await fetch(`${API_URL}/free-tools/categories`, {
      signal: combinedSignal,
      headers: { Accept: 'application/json' },
    })
    clearTimeout(timeoutId)
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const json = (await res.json()) as FreeToolsApiResponse
    if (!json.success || !json.data) throw new Error(json.error ?? 'Bad envelope')
    return mergeWithFallback(json.data)
  } catch (err: any) {
    clearTimeout(timeoutId)
    // React Query cancellation (unmount / navigation) — do NOT fallback, let query die quietly
    if (signal?.aborted) {
      throw err
    }
    if (err?.name === 'AbortError') {
      if (timeoutCtrl.signal.aborted) {
        console.warn('[free-tools] fetch timeout 12s, falling back to seed')
      } else {
        console.warn('[free-tools] fetch aborted (not outer), falling back to seed')
      }
      return dedupCategories(FREE_TOOLS_FALLBACK)
    }
    console.warn('[free-tools] falling back to seed:', err)
    return dedupCategories(FREE_TOOLS_FALLBACK)
  }
}

/**
 * Fire-and-forget click tracker. Errors are swallowed — opening the
 * tool URL is the user's primary action and shouldn't depend on this.
 */
export function trackToolClick(toolId: string): void {
  if (!API_URL) return
  // `keepalive: true` lets the request finish after navigation/window close.
  void fetch(`${API_URL}/free-tools/tools/${toolId}/click`, {
    method: 'POST',
    keepalive: true,
    headers: { Accept: 'application/json' },
  }).catch(() => {
    /* swallowed by design */
  })
}
